import * as THREE from 'three';
import { LITTER_GO, POST_MARK, WALK_DOWN, WALK_ON, walkLit, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** Stones coming: three small stones falling. */
const STONES = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="4.5" cy="10.5" r="2.2" fill="currentColor"/><circle cx="10" cy="7" r="1.8" fill="currentColor"/><circle cx="12.5" cy="12" r="1.5" fill="currentColor"/><path d="M5.5 2.5 4.6 6.4M10.8 1.5 10.2 4.2" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>';
/** The moon. */
const MOON = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10.5 2.2a6 6 0 1 0 3.3 9.7A5 5 0 0 1 10.5 2.2z" fill="currentColor"/></svg>';
/** Planks. */
const PLANKS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 4.5h12M2 8h5M9.5 8H14M2 11.5h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
/** The bone mangonel: its arm over its frame. */
const MANGONEL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 13.5h11M4 13.5l3-5 3 5" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M7 8.5 13 3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="13.2" cy="2.8" r="1.5" fill="currentColor"/></svg>';
/** Someone hanging from a plank. */
const HANG = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 3h13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="8" cy="8" r="1.7" fill="currentColor"/><path d="M6 4l1.3 3M10 4 8.7 7M8 9.7v3.6M8 13.3l-1.6 1.6M8 13.3l1.6 1.6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
/** Go: an arrow up the way; wait: a raised hand. */
const GO = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 13.5V3M3.8 7.2 8 3l4.2 4.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const WAIT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 14V7.5M5 7.5V4a1 1 0 0 1 2 0v3M7 7V3a1 1 0 0 1 2 0v4M9 7V3.6a1 1 0 0 1 2 0V8M11 8V6a1 1 0 0 1 2 0v3.5c0 2.5-2 4.5-4.5 4.5H8c-1.8 0-3-1.2-3-2.5" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
/** A post's mirror by its position: down (onto its span), on (to the next post), back (to the moon). */
const POSTS = [
  '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="5" rx="4.2" ry="2" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M8 7.5v6M5.2 11 8 13.8 10.8 11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="5" cy="8" rx="2" ry="4.2" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M7.5 8h6M11 5.2 13.8 8 11 10.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="11" cy="8" rx="2" ry="4.2" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M8.5 8h-6M5 5.2 2.2 8 5 10.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
];

type Mark = { el: HTMLButtonElement; at: number; point: THREE.Vector3; ico: HTMLElement; state: HTMLElement | null };

const secs = (ticks: number): number => Math.max(0, Math.ceil(ticks / 12));

/**
 * The cleric's last road on screen, chapter 2 (docs/sendas/clerigo.md, «La pasarela»), inside a path's own plate under
 * the stretcher's rows (game/camino-hud.ts) and over the board:
 *
 * - the cliff path: when the slingers throw next and whether the stretcher is under cover then (red while it is out);
 *   a mark on the stretcher while it waits in a chapel, to tell it to go.
 * - the walkway: which span the moon lies on, each span clean or under ink (and when a dark one covers again); a mark on
 *   each post with its mirror's position (down, on, back); Piet hanging, the time left to get him up.
 * - the bridge (the test): when the mangonel throws next (red while a stone is in the air), the planks torn, the
 *   stretcher over a gap and the seconds before it falls; a mark on the stretcher to say go or wait.
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class PasarelaHud {
  readonly panel: HTMLElement;
  private kind: 'ridge' | 'walk' | 'bridge';
  private v = new THREE.Vector3();
  private rowA = h('div.bh-row.al-waverow.ps-a');
  private textA = h('span.al-wave');
  private rowB = h('div.bh-row.al-waverow.ps-b');
  private textB = h('span.al-wave');
  private rowC = h('div.bh-row.al-waverow.ps-c');
  private textC = h('span.al-wave');
  private spans: HTMLElement[] = [];
  private marks: Mark[] = [];
  private key = '';

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const r = s.rules?.senda?.litter;
    this.kind = r?.kind === 'walk' ? 'walk' : r?.kind === 'bridge' ? 'bridge' : 'ridge';
    const rows: HTMLElement[] = [];
    if (this.kind === 'ridge') {
      this.rowA.append(h('span.bh-ico', { html: STONES }), h('span.bh-label', {}, t('senda.camino.slingers')), this.textA);
      rows.push(this.rowA);
      this.addMark(LITTER_GO, { x: 0, y: 0 }, GO, t('senda.camino.go'), t('senda.camino.goHint'));
    } else if (this.kind === 'walk' && r?.walk) {
      this.rowA.append(h('span.bh-ico', { html: MOON }), h('span.bh-label', {}, t('senda.camino.moon')), this.textA);
      const chips = h('span.ps-spans');
      r.walk.spans.forEach((_, i) => {
        const c = h('span.ps-span', {}, String(i + 1));
        this.spans.push(c);
        chips.append(c);
      });
      this.rowB.append(h('span.bh-ico', { html: PLANKS }), h('span.bh-label', {}, t('senda.camino.spans')), chips, this.textB);
      this.rowC.append(h('span.bh-ico', { html: HANG }), this.textC);
      rows.push(this.rowA, this.rowB, this.rowC);
      r.walk.posts.forEach((p, i) => this.addMark(POST_MARK + i, p, POSTS[0] as string, t('senda.camino.post', { n: i + 1 }), t('senda.camino.postHint')));
    } else {
      this.rowA.append(h('span.bh-ico', { html: MANGONEL }), h('span.bh-label', {}, t('senda.camino.stone')), this.textA);
      this.rowB.append(h('span.bh-ico', { html: PLANKS }), h('span.bh-label', {}, t('senda.camino.planks')), this.textB);
      rows.push(this.rowA, this.rowB);
      this.addMark(LITTER_GO, { x: 0, y: 0 }, GO, t('senda.camino.go'), t('senda.camino.goHint'));
    }
    this.panel = h(`div.bh.ps-panel.ps-${this.kind}`, {}, ...rows);
  }

  private addMark(at: number, p: { x: number; y: number }, ico: string, name: string, hint: string): void {
    const icon = h('span.cm-ico', { html: ico });
    const state = at >= POST_MARK && at < LITTER_GO ? h('b.ps-state') : null;
    const el = h(
      `button.al-stone.cm-mark.ps-mark${at === LITTER_GO ? '.ps-go' : '.ps-post'}`,
      { type: 'button', 'data-test': `pasarela-mark-${at}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      icon,
      h('b.mn-name.cm-name', {}, name),
      ...(state ? [state] : []),
    ) as HTMLButtonElement;
    this.board.append(el);
    this.marks.push({ el, at, point: simToWorld(p.x, p.y, new THREE.Vector3(), at === LITTER_GO ? 3.0 : 2.7), ico: icon, state });
  }

  update(s: BattleState): void {
    const r = s.rules?.senda?.litter;
    const w = s.senda?.litter;
    const p = w?.pas;
    if (!r || !w || !p) return;
    const set = (el: HTMLElement, text: string): void => {
      if (el.textContent !== text) el.textContent = text;
    };
    if (this.kind === 'ridge' && r.ridge) {
      const V = r.ridge.volley;
      const left = secs(p.volley - s.waveT);
      const covered = r.ridge.chapels.some((c) => Math.hypot(w.x - c.x, w.y - c.y) <= (r.ridge?.shade ?? 0));
      const warned = p.marks.length > 0;
      set(this.textA, warned ? t(covered ? 'senda.camino.slingCover' : 'senda.camino.slingOpen', { n: left }) : t('senda.camino.slingNext', { n: secs(p.volley - V.warn - s.waveT) + secs(V.warn) }));
      this.rowA.classList.toggle('warn', warned && !covered);
      this.rowA.classList.toggle('good', warned && covered);
    } else if (this.kind === 'walk' && r.walk) {
      const W = r.walk;
      const lit = walkLit(w.turns);
      set(this.textA, lit >= 0 ? t('senda.camino.moonOn', { n: lit + 1 }) : t('senda.camino.moonNone'));
      this.rowA.classList.toggle('warn', lit < 0);
      let cover = '';
      this.spans.forEach((c, i) => {
        const clean = (p.clean[i] ?? 0) >= W.burn;
        const burning = !clean && lit === i;
        const dark = clean && lit !== i ? W.cover - (p.dark[i] ?? 0) : 0;
        const st = clean ? (dark > 0 ? 'dark' : 'clean') : burning ? 'burn' : 'ink';
        if (c.dataset.st !== st) {
          c.dataset.st = st;
          c.className = `ps-span ${st}`;
        }
        if (dark > 0 && !cover) cover = t('senda.camino.spanCover', { n: secs(dark) });
      });
      set(this.textB, cover);
      this.rowB.classList.toggle('warn', !!cover);
      const hanging = p.slip >= 0;
      set(this.textC, hanging ? `${t('senda.camino.hanging')} · ${secs(W.slip.bar - (s.tick - p.slip))} s` : '');
      this.rowC.classList.toggle('warn', hanging);
      this.rowC.style.display = hanging ? '' : 'none';
    } else if (this.kind === 'bridge' && r.span) {
      const M = r.span.mangonel;
      const st = p.stones[0];
      const dead = !s.enemies.some((e) => e.id === p.boss && e.hp > 0);
      set(this.textA, dead ? t('senda.camino.stoneDown') : st ? t('senda.camino.stoneFly', { n: secs(st.at - s.waveT) }) : t('senda.camino.stoneIn', { n: secs(p.stone - s.waveT) }));
      this.rowA.classList.toggle('warn', !!st);
      const torn = p.torn.reduce((n, x) => n + x, 0);
      set(this.textB, p.hang > 0 ? t('senda.camino.overHole', { n: secs(r.span.hang - p.hang) }) : p.mend >= 0 ? t('senda.camino.mending') : torn ? t('senda.camino.torn', { n: torn }) : t('senda.camino.whole'));
      this.rowB.classList.toggle('warn', p.hang > 0);
      void M;
    }
    // The marks: the stretcher's (go, or wait on the bridge), the posts' positions.
    const key = `${p.wait}|${p.go}|${w.done}|${w.turns.join('')}|${w.job}|${s.result}`;
    if (key !== this.key) {
      this.key = key;
      for (const m of this.marks) {
        if (m.at === LITTER_GO) {
          const gone = w.done >= 0 || (r.ridge ? p.wait < 0 : false);
          m.el.classList.toggle('gone', gone);
          const wait = !!r.span && p.go;
          m.el.classList.toggle('wait', wait);
          const ico = wait ? WAIT : GO;
          if (m.ico.dataset.k !== (wait ? 'w' : 'g')) {
            m.ico.dataset.k = wait ? 'w' : 'g';
            m.ico.innerHTML = ico;
            const name = m.el.querySelector('.cm-name');
            if (name) name.textContent = t(wait ? 'senda.camino.wait' : 'senda.camino.go');
            m.el.title = t(wait ? 'senda.camino.waitHint' : 'senda.camino.goHint');
          }
        } else {
          const i = m.at - POST_MARK;
          const tn = w.turns[i] ?? WALK_ON;
          if (m.ico.dataset.k !== String(tn)) {
            m.ico.dataset.k = String(tn);
            m.ico.innerHTML = POSTS[tn] as string;
          }
          if (m.state) m.state.textContent = t(`senda.camino.down${tn}`);
          m.el.classList.toggle('down', tn === WALK_DOWN);
        }
        m.el.disabled = s.result !== 'running';
        remeasureMark(m.el);
      }
    }
    for (const m of this.marks) {
      const sent = w.job === m.at;
      m.el.classList.toggle('sent', sent);
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, r.walk?.stand ?? 12)).toFixed(2) : '0');
      // The stretcher's mark goes with it.
      if (m.at === LITTER_GO) simToWorld(w.x, w.y, m.point, 3.0);
    }
  }

  /** Every frame: the marks over their places. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      this.v.copy(m.point).project(camera);
      if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      pinMark(m.el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
    }
  }
}
